import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { executeBulkAction as executeBulk } from "@/utils/bulk";
import { withSelectedIds } from "@/utils/selectionStats";
import { orgStorage } from '@/utils/org-storage';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const {
    getStorageKey,
    loadFiltersFromStorage,
    saveFiltersToStorage,
    mergeUserAndSectionFilters,
    extractUserFiltersFromEffective,
    getSectionConstraintKeys,
    omitFilterKeys,
} = utils;

/** Shared across all customer-order tabs (section constraints applied at query time). */
const CUSTOMERORDERS_FILTERS_STORAGE_KEY = getStorageKey('customerorders');
const DEFAULT_USER_FILTERS = { limit: 20, ordering: '-order_id' };

const CHART_SETTINGS_STORAGE_KEY = 'customerorders-chart-settings';

let ordersStatsRequestId = 0;
let ordersMonthlyStatsRequestId = 0;

function userCanViewCustomerOrdersStats(rootGetters) {
    const permissions = rootGetters["session/current_user_permissions"] || [];
    return (
        permissions.indexOf("order.view_order") !== -1
        || permissions.indexOf("order.view_customerorder") !== -1
    );
}

// Helper to load chart settings from localStorage
const loadChartSettings = () => {
    try {
        const saved = orgStorage.getItem(CHART_SETTINGS_STORAGE_KEY);
        if (saved) {
            return JSON.parse(saved);
        }
    } catch (e) {
        console.warn('Failed to load chart settings from orgStorage:', e);
    }
    return null;
};

// Helper to save chart settings to localStorage
const saveChartSettings = (settings) => {
    try {
        const toSave = { ...settings };
        // Don't save range if both min and max are null
        if (toSave.range && toSave.range.min === null && toSave.range.max === null) {
            delete toSave.range;
        }
        orgStorage.setItem(CHART_SETTINGS_STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) {
        console.warn('Failed to save chart settings to orgStorage:', e);
    }
};

const state = {
    section: null,
    customer: null,
    orders: [],
    /** User-chosen filters persisted across tabs (no section constraints). */
    orders_user_filters: { ...DEFAULT_USER_FILTERS },
    /** Effective filters bound to Table / API (user + section overlay). */
    orders_filters: { limit: 20, offset: 0, ordering: '-order_id' },
    orders_user_filters_hydrated: false,
    orders_count: 0,
    orders_loading: false,
    orders_loading_errors: null,
    orders_cancel_source: null,

    orders_stats: {},
    orders_stats_loaded: false,
    orders_stats_loading: false,
    orders_stats_loading_errors: null,
    orders_stats_cancel_source: null,

    orders_monthly_stats: null,
    orders_monthly_stats_loading: false,
    orders_monthly_stats_errors: null,
    orders_monthly_stats_cancel_source: null,

    orders_stats_resolution: "month",
    orders_stats_group_by: "validated_date",
    orders_stats_mode: "aggregate",
    orders_stats_range: { min: null, max: null },

    selected_items: [],
    bulk_action_loading: false,
};

function customerOrdersStatusIncludesCanceled(status) {
    if (status == null || status === "") {
        return false;
    }
    const statuses = Array.isArray(status) ? status : [status];
    return statuses.includes("canceled");
}

function customerOrdersHasStatusFilter(status) {
    if (status == null || status === "") {
        return false;
    }
    if (Array.isArray(status)) {
        return status.length > 0;
    }
    return true;
}

function normalizeCustomerOrdersSection(section) {
    if (section === "canceled") {
        return "all";
    }
    return section || "all";
}

function normalizeCustomerOrdersFilters(filters, section) {
    const next = { ...(filters || {}) };

    if (section === "all") {
        const hasStatusFilter = customerOrdersHasStatusFilter(next.status);
        if (!hasStatusFilter || customerOrdersStatusIncludesCanceled(next.status)) {
            next.include_canceled = true;
        } else {
            next.include_canceled = false;
        }
    }

    return next;
}

function buildCustomerOrdersApiParams(filters, section) {
    const normalizedSection = section ?? "all";
    return normalizeCustomerOrdersFilters(
        mergeUserAndSectionFilters(filters, getSectionFilters(normalizedSection)),
        normalizedSection,
    );
}

function loadSharedCustomerOrdersUserFilters(section) {
    const shared = loadFiltersFromStorage(
        CUSTOMERORDERS_FILTERS_STORAGE_KEY,
        null,
    );
    if (shared) {
        return extractUserFiltersFromEffective(
            shared,
            getSectionFilters(section || 'all'),
            {},
        );
    }
    // Migrate legacy per-section prefs once.
    const legacy = loadFiltersFromStorage(
        getStorageKey('customerorders', section || 'all'),
        { ...DEFAULT_USER_FILTERS },
    );
    return extractUserFiltersFromEffective(
        legacy,
        getSectionFilters(section || 'all'),
        {},
    );
}

function persistCustomerOrdersUserFilters(userFilters) {
    saveFiltersToStorage(CUSTOMERORDERS_FILTERS_STORAGE_KEY, userFilters);
}

function rebuildCustomerOrdersFilters(state, { resetOffset = false } = {}) {
    const section = state.section || 'all';
    const previousOffset = state.orders_filters?.offset ?? 0;
    state.orders_filters = normalizeCustomerOrdersFilters(
        mergeUserAndSectionFilters(
            state.orders_user_filters,
            getSectionFilters(section),
        ),
        section,
    );
    state.orders_filters.offset = resetOffset ? 0 : previousOffset;
}

function buildCustomerOrdersStatsApiParams(filters, section) {
    const params = { ...buildCustomerOrdersApiParams(filters, section) };
    delete params.limit;
    delete params.offset;
    delete params.ordering;
    return params;
}

let getSectionFilters = function (section) {
    const filters = {
        exclude_readonly: false,
        suborder: false,
        ordering: '-order_id',
        limit: 20,
        offset: 0,
        include_canceled: false,
    };
    switch (section) {
        case 'all':
            filters.include_canceled = true;
            break;
        case 'waiting':
            filters.status = ['waiting'];
            break;
        case 'drafts':
            filters.status = ['draft'];
            break;
        case 'tohandle':
            filters.status = ['to_be_processed'];
            break;
        case 'pending':
            filters.status = ['waiting'];
            break;
        case 'processing':
            filters.status = ['processing'];
            break;
        case 'waitingpayment':
            filters.status = ['completed'];
            filters.invoices_ready = true;
            filters.invoices_sent = true;
            filters.paid = false;
            break;
        case 'invoicepending':
            filters.status = ['completed'];
            filters.invoices_ready = false;
            filters.invoices_sent = false;
            filters.paid = false;
            break;
        case 'invoicenotsent':
            filters.status = ['completed'];
            filters.invoices_ready = true;
            filters.invoices_sent = false;
            filters.paid = false;
            break;
    }
    return filters;

};

/** Session counter key for a customer-orders tab — same keys as tab badges. */
const SECTION_STATS_KEYS = {
    all: 'customerorders',
    drafts: 'draft_customerorders',
    tohandle: 'tohandle_customerorders',
    processing: 'processing_customerorders',
    waitingpayment: 'waitingpayment_customerorders',
    invoicepending: 'invoicepending_customerorders',
    invoicenotsent: 'invoicenotsent_customerorders',
    handling: 'handling_customerorders',
    pending: 'pending_customerorders',
    ready: 'ready_customerorders',
    waiting: 'waiting_customerorders',
};

const EMPTY_ORDERS_STATS = {
    count: 0,
    orders_without_margin: 0,
    orders_unassigned: 0,
    total_amount_excl_vat_sum: 0,
    total_amount_incl_vat_sum: 0,
    total_agent_commission_sum: 0,
    total_margin_excl_vat_sum: 0,
    average_basket_excl_vat: null,
    average_order_value_excl_vat: null,
    total_revenue_sum: 0,
    revenue_ratio: null,
    total_agent_commission_ratio: null,
    total_margin_ratio: null,
    total_items: 0,
};

const getSectionCustomerOrdersCount = (stats, section) => {
    if (!stats) {
        return null;
    }
    const key = SECTION_STATS_KEYS[section];
    if (!key) {
        return null;
    }
    const count = stats[key];
    return count == null ? null : count;
};

const isCustomerOrdersSectionKnownEmpty = (rootState, section) => {
    if (!rootState.session?.stats_loaded) {
        return false;
    }
    return getSectionCustomerOrdersCount(rootState.session.stats, section) === 0;
};

const applyEmptyCustomerOrdersState = (commit, state) => {
    if (state.orders_cancel_source) {
        state.orders_cancel_source.cancel("canceled");
        commit('updateOrdersCancelSource', null);
    }
    if (state.orders_stats_cancel_source) {
        state.orders_stats_cancel_source.cancel("canceled");
        commit('updateOrdersStatsCancelSource', null);
    }
    commit('updateOrders', []);
    commit('updateOrdersCount', 0);
    commit('updateOrdersLoading', false);
    commit('updateOrdersLoadingErrors', null);
    commit('updateOrdersStats', { ...EMPTY_ORDERS_STATS });
    commit('updateOrdersStatsLoading', false);
};

const mutations = {
    updateOrdersStats(state, orders_stats) {
        state.orders_stats = orders_stats;
        state.orders_stats_loaded = true;
    },
    updateOrdersStatsLoading(state, loading) {
        state.orders_stats_loading = loading;
    },
    updateOrdersStatsLoadingErrors(state, errors) {
        state.orders_stats_loading_errors = errors;
    },
    updateOrdersStatsCancelSource(state, cancel_source) {
        state.orders_stats_cancel_source = cancel_source;
    },
    updateOrdersMonthlyStats(state, payload) {
        state.orders_monthly_stats = payload;
    },
    updateOrdersMonthlyStatsLoading(state, loading) {
        state.orders_monthly_stats_loading = loading;
    },
    updateOrdersMonthlyStatsErrors(state, errors) {
        state.orders_monthly_stats_errors = errors;
    },
    updateOrdersMonthlyStatsCancelSource(state, source) {
        state.orders_monthly_stats_cancel_source = source;
    },
    updateSection(state, section) {
        state.orders = [];
        state.orders_count = 0;
        state.section = normalizeCustomerOrdersSection(section);
        if (!state.orders_user_filters_hydrated) {
            state.orders_user_filters = {
                ...DEFAULT_USER_FILTERS,
                ...loadSharedCustomerOrdersUserFilters(state.section),
            };
            state.orders_user_filters_hydrated = true;
            persistCustomerOrdersUserFilters(state.orders_user_filters);
        }
        rebuildCustomerOrdersFilters(state, { resetOffset: true });
    },


    updateOrders(state, orders) {
        state.orders = orders;
        state.selected_items = state.selected_items?.map((selected_item) => {
            return orders.find((order) => order.id === selected_item.id) || selected_item;
        });
    },
    updateOrdersCount(state, count) {
        state.orders_count = count;
    },
    updateOrdersLoading(state, loading) {
        state.orders_loading = loading;
    },
    updateOrdersLoadingErrors(state, errors) {
        state.orders_loading_errors = errors;
    },
    updateOrdersFilters(state, orders_filters) {
        const section = state.section || 'all';
        state.orders_user_filters = extractUserFiltersFromEffective(
            orders_filters,
            getSectionFilters(section),
            state.orders_user_filters,
        );
        state.orders_user_filters_hydrated = true;
        persistCustomerOrdersUserFilters(state.orders_user_filters);
        state.orders_filters = normalizeCustomerOrdersFilters(
            mergeUserAndSectionFilters(
                state.orders_user_filters,
                getSectionFilters(section),
            ),
            section,
        );
        state.orders_filters.offset = orders_filters?.offset ?? 0;
    },

    updateOrdersCancelSource(state, source) {
        state.orders_cancel_source = source;
    },

    updateCustomer(state, customer) {
        state.customer = customer;
    },

    setOrdersCompletedDateRange(state, { min, max }) {
        const patchDateRange = (filters) => {
            const next = { ...(filters || {}) };
            if (min) {
                next.completed_date_min = min;
            } else {
                delete next.completed_date_min;
            }
            if (max) {
                next.completed_date_max = max;
            } else {
                delete next.completed_date_max;
            }
            return next;
        };
        state.orders_user_filters = patchDateRange(state.orders_user_filters);
        persistCustomerOrdersUserFilters(state.orders_user_filters);
        const offset = state.orders_filters?.offset ?? 0;
        rebuildCustomerOrdersFilters(state);
        state.orders_filters.offset = offset;
    },

    setOrdersStatsResolution(state, resolution) {
        state.orders_stats_resolution = resolution || "month";
        saveChartSettings({
            resolution: state.orders_stats_resolution,
            group_by: state.orders_stats_group_by,
            mode: state.orders_stats_mode,
            range: state.orders_stats_range,
        });
    },

    setOrdersStatsGroupBy(state, groupBy) {
        const previous = state.orders_stats_group_by || "validated_date";
        state.orders_stats_group_by = groupBy || "validated_date";

        // When switching grouping, remap existing range from old field to new field in filters
        if (state.orders_stats_range) {
            const { min, max } = state.orders_stats_range;
            const patchGroupByRange = (filters) => {
                const next = { ...(filters || {}) };
                const prevMinField = `${previous}_min`;
                const prevMaxField = `${previous}_max`;
                delete next[prevMinField];
                delete next[prevMaxField];
                delete next[previous];

                const newMinField = `${state.orders_stats_group_by}_min`;
                const newMaxField = `${state.orders_stats_group_by}_max`;

                if (min) {
                    next[newMinField] = min;
                }
                if (max) {
                    next[newMaxField] = max;
                }

                if (min || max) {
                    next[state.orders_stats_group_by] = { min, max };
                } else {
                    delete next[state.orders_stats_group_by];
                }
                return next;
            };
            state.orders_user_filters = patchGroupByRange(state.orders_user_filters);
            persistCustomerOrdersUserFilters(state.orders_user_filters);
            const offset = state.orders_filters?.offset ?? 0;
            rebuildCustomerOrdersFilters(state);
            state.orders_filters.offset = offset;
        }
        saveChartSettings({
            resolution: state.orders_stats_resolution,
            group_by: state.orders_stats_group_by,
            mode: state.orders_stats_mode,
            range: state.orders_stats_range,
        });
    },

    setOrdersStatsMode(state, mode) {
        state.orders_stats_mode = mode || "aggregate";
        saveChartSettings({
            resolution: state.orders_stats_resolution,
            group_by: state.orders_stats_group_by,
            mode: state.orders_stats_mode,
            range: state.orders_stats_range,
        });
    },

    setOrdersStatsRange(state, range) {
        const min = range?.min || null;
        const max = range?.max || null;

        state.orders_stats_range = { min, max };

        // Keep list filters in sync with the chart range for the active grouping field
        const groupBy = state.orders_stats_group_by || "validated_date";
        const minField = `${groupBy}_min`;
        const maxField = `${groupBy}_max`;

        const patchStatsRange = (filters) => {
            const next = { ...(filters || {}) };
            if (min) {
                next[minField] = min;
            } else {
                delete next[minField];
            }
            if (max) {
                next[maxField] = max;
            } else {
                delete next[maxField];
            }
            // Also store a synthetic daterange value so the Filters UI can display it
            if (min || max) {
                next[groupBy] = { min, max };
            } else {
                delete next[groupBy];
            }
            return next;
        };
        state.orders_user_filters = patchStatsRange(state.orders_user_filters);
        persistCustomerOrdersUserFilters(state.orders_user_filters);
        const offset = state.orders_filters?.offset ?? 0;
        rebuildCustomerOrdersFilters(state);
        state.orders_filters.offset = offset;
        saveChartSettings({
            resolution: state.orders_stats_resolution,
            group_by: state.orders_stats_group_by,
            mode: state.orders_stats_mode,
            range: state.orders_stats_range,
        });
    },

    updateSelectedItems(state, selected_items) {
        state.selected_items = selected_items || [];
    },

    updateBulkActionLoading(state, loading) {
        state.bulk_action_loading = !!loading;
    },

    hydrateChartSettingsFromStorage(state) {
        const saved = loadChartSettings();
        if (!saved) {
            return;
        }
        if (saved.resolution) {
            state.orders_stats_resolution = saved.resolution;
        }
        if (saved.group_by) {
            state.orders_stats_group_by = saved.group_by;
        }
        if (saved.mode) {
            state.orders_stats_mode = saved.mode;
        }
        if (saved.range) {
            state.orders_stats_range = saved.range;
        }
    },

};

const actions = {
    hydrateOrgStorageSettings({ commit }) {
        commit('hydrateChartSettingsFromStorage');
    },

    async init({ commit, dispatch, state }, params) {
        // Initialize the store with section from route params or default
        const section = normalizeCustomerOrdersSection(params?.section || state.section);
        if (section !== state.section) {
            await commit('updateSection', section);
        }
        await dispatch('refreshOrdersPage');
        dispatch('session/fetchStats', null, { root: true });
    },
    async changeSection({ commit, dispatch, state }, mode) {
        const section = normalizeCustomerOrdersSection(mode);
        if (section != state.section) {
            await commit('updateSection', section);
        }
        await dispatch('refreshOrdersPage');
        dispatch('session/fetchStats', null, { root: true });
    },

    async refreshOrdersPage({ commit, dispatch, state, rootState }) {
        if (isCustomerOrdersSectionKnownEmpty(rootState, state.section)) {
            applyEmptyCustomerOrdersState(commit, state);
            return;
        }
        commit('updateOrdersStatsLoading', true);
        await Promise.all([
            dispatch('fetchOrders'),
            dispatch('fetchOrdersStats'),
        ]);
    },

    exportOrdersAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = { ...buildCustomerOrdersApiParams(state.orders_filters, state.section) };
            delete filters.offset;
        } else {
            filters.ordering = '-order_id';
            filters.suborder = false;
            filters.exclude_readonly = false;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;

        $utils.downloadFile({
            url: "/api/customerorders/",
            params: filters,
        }, $utils.getFilename('customerOrders'));
    },

    exportOrderItemsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            const skip = new Set(["limit", "offset", "ordering"]);
            const requestFilters = buildCustomerOrdersApiParams(
                state.orders_filters,
                state.section,
            );
            for (const key in requestFilters) {
                if (skip.has(key)) {
                    continue;
                }
                const value = requestFilters[key];
                if (value === null || value === undefined || value === "") {
                    continue;
                }
                filters["order_" + key] = value;
            }
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        delete filters.offset;
        delete filters.order_offset;

        $utils.downloadFile({
            url: "/api/customerorderitems/",
            params: filters,
        }, $utils.getFilename('customerOrderItems'));
    },

    async fetchOrders({ commit, dispatch, state, rootState }, params) {
        if (isCustomerOrdersSectionKnownEmpty(rootState, state.section)) {
            applyEmptyCustomerOrdersState(commit, state);
            return;
        }

        commit('updateOrdersLoading', true);
        commit('updateOrdersLoadingErrors', null);

        if (state.orders_cancel_source) {
            state.orders_cancel_source.cancel("canceled");
        }
        
        const cancelSource = axios.CancelToken.source();
        commit('updateOrdersCancelSource', cancelSource);
        try {
            const response = await axios.get('/api/customerorders/', {
                params: buildCustomerOrdersApiParams(
                    state.orders_filters,
                    state.section,
                ),
                cancelToken: state.orders_cancel_source?.token,
            });
            commit('updateOrdersLoading', false);
            commit('updateOrders', response.data.results);
            commit('updateOrdersCount', response.data.count);
            commit('updateOrdersCancelSource', null);

            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateOrdersLoadingErrors', error.details);
            commit('updateOrdersLoading', false);
            commit('updateOrdersCancelSource', null);
            return Promise.reject(error);
        }
    },

    async bulkAction({ commit, dispatch, state }, params) {
        commit("updateBulkActionLoading", true);
        try {
            const result = await executeBulk({
                bulkActionUrl: "/api/customerorders/bulk/",
                action: params.action,
                instances: state.selected_items.map(item => item.id),
                payload: params.payload || {},
                context: params.context,
                getIdFn: params.getIdFn,
                successMessage: params.successMessage,
            });
            const items = state.selected_items;
            commit("updateSelectedItems", []);
            dispatch("refreshOrdersPage");
            dispatch("session/fetchStats", null, { root: true });
            return { ...result, items };
        } catch (xhr_error) {
            throw utils.handleError(xhr_error);
        } finally {
            commit("updateBulkActionLoading", false);
        }
    },

    async assignOrder({ commit, dispatch, state }, params) {
        const url = `/api/customerorders/${params.order.id}/assign/`;

        try {
            const response = await axios.put(url, { user: params.user });
            dispatch('refreshOrdersPage');
            dispatch('session/fetchStats', null, { root: true });
            return response;
        } catch (xhr_error) {
            return Promise.reject(utils.handleError(xhr_error));
        }
    },

    async fetchOrdersStats({ commit, state, rootState, rootGetters }) {
        if (!userCanViewCustomerOrdersStats(rootGetters)) {
            return;
        }

        if (isCustomerOrdersSectionKnownEmpty(rootState, state.section)) {
            commit('updateOrdersStats', { ...EMPTY_ORDERS_STATS });
            commit('updateOrdersStatsLoading', false);
            return;
        }

        const requestId = ++ordersStatsRequestId;
        commit('updateOrdersStatsLoading', true);
        commit('updateOrdersStatsLoadingErrors', null);

        try {
            const response = await axios.get('/api/customerorders/stats/', {
                params: withSelectedIds(
                    buildCustomerOrdersStatsApiParams(
                        state.orders_filters,
                        state.section,
                    ),
                    state.selected_items,
                ),
            });
            if (requestId !== ordersStatsRequestId) {
                return;
            }
            commit('updateOrdersStats', response.data);
            return response;
        } catch (xhr_error) {
            if (requestId !== ordersStatsRequestId) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateOrdersStatsLoadingErrors', error.details);
            throw error;
        } finally {
            if (requestId === ordersStatsRequestId) {
                commit('updateOrdersStatsLoading', false);
            }
        }
    },

    async fetchOrdersMonthlyStats({ commit, state, rootGetters }) {
        if (!userCanViewCustomerOrdersStats(rootGetters)) {
            return;
        }

        const requestId = ++ordersMonthlyStatsRequestId;
        commit('updateOrdersMonthlyStatsLoading', true);
        commit('updateOrdersMonthlyStatsErrors', null);

        try {
            const params = Object.assign(
                {},
                buildCustomerOrdersStatsApiParams(
                    state.orders_filters,
                    state.section,
                ),
                {
                resolution: state.orders_stats_resolution,
                group_by: state.orders_stats_group_by,
                chart_mode: state.orders_stats_mode,
            },
            );

            const dateFields = ["completed_date", "validated_date", "paid_date"];
            dateFields.forEach((field) => {
                delete params[`${field}_min`];
                delete params[`${field}_max`];
            });

            const rangeField = state.orders_stats_group_by || "completed_date";

            if (state.orders_stats_range?.min) {
                params[`${rangeField}_min`] = state.orders_stats_range.min;
            }

            if (state.orders_stats_range?.max) {
                params[`${rangeField}_max`] = state.orders_stats_range.max;
            }

            const response = await axios.get('/api/customerorders/stats/monthly/', {
                params,
            });
            if (requestId !== ordersMonthlyStatsRequestId) {
                return;
            }
            commit('updateOrdersMonthlyStats', response.data);
            return response;
        } catch (xhr_error) {
            if (requestId !== ordersMonthlyStatsRequestId) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateOrdersMonthlyStatsErrors', error.details);
            throw error;
        } finally {
            if (requestId === ordersMonthlyStatsRequestId) {
                commit('updateOrdersMonthlyStatsLoading', false);
            }
        }
    },

    async duplicateOrder({ commit, dispatch, state }, params) {
        try {
            const orderId = params?.order?.id ?? params?.order_id;
            if (!orderId) {
                return Promise.reject(new Error("Order id is required"));
            }
            const url = `/api/customerorders/${orderId}/duplicate/`;
            const response = await axios.post(url);
            dispatch("refreshOrdersPage");
            dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            return Promise.reject(utils.handleError(xhr_error));
        }
    },

    setCompletedDateRange({ commit, dispatch }, range) {
        const formatDate = (date) => {
            if (!date) {
                return null;
            }
            if (typeof date === "string") {
                return date.slice(0, 10);
            }
            return date.toISOString().slice(0, 10);
        };

        const min = formatDate(range?.min);
        const max = formatDate(range?.max);

        commit("setOrdersCompletedDateRange", { min, max });
        dispatch("refreshOrdersPage");
    },

    setOrdersStatsResolution({ commit, dispatch }, resolution) {
        commit("setOrdersStatsResolution", resolution);
        dispatch("fetchOrdersMonthlyStats");
    },

    setOrdersStatsGroupBy({ commit, dispatch }, groupBy) {
        commit("setOrdersStatsGroupBy", groupBy);
        dispatch("fetchOrdersMonthlyStats");
    },

    setOrdersStatsMode({ commit, dispatch }, mode) {
        commit("setOrdersStatsMode", mode || "aggregate");
        dispatch("fetchOrdersMonthlyStats");
    },

    setOrdersStatsRange({ commit, dispatch }, range) {
        const formatDate = (value) => {
            if (!value) {
                return null;
            }
            if (typeof value === "string") {
                return value.slice(0, 10);
            }
            if (value instanceof Date) {
                return value.toISOString().slice(0, 10);
            }
            return null;
        };

        commit("setOrdersStatsRange", {
            min: formatDate(range?.min),
            max: formatDate(range?.max),
        });

        dispatch("fetchOrdersMonthlyStats");
    },
};

const getters = {
    /**
     * Filters bound to Table UI: section constraints (status, paid, …) are hidden
     * so tabs don't surface forced values as user chips. API still uses orders_filters.
     */
    orders_table_filters(state) {
        const section = state.section || 'all';
        return omitFilterKeys(
            state.orders_filters,
            getSectionConstraintKeys(getSectionFilters(section)),
        );
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
